import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortesOuvertesCancel() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [info, setInfo] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/portes-ouvertes/cancel/${token}`);
        if (!res.ok) throw new Error('Lien invalide');
        const data = await res.json();
        if (data.already_cancelled) {
          setStatus('already_cancelled');
        } else {
          setInfo(data);
          setStatus('ready');
        }
      } catch {
        setStatus('error');
        setError("Ce lien d'annulation est invalide ou a expiré.");
      }
    };
    fetchInfo();
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/public/portes-ouvertes/cancel/${token}`, { method: 'POST' });
      if (!res.ok) throw new Error('Erreur');
      setStatus('cancelled');
    } catch {
      setError("Erreur lors de l'annulation. Veuillez réessayer.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-lg">Portes Ouvertes</CardTitle>
          <CardDescription>Annulation d'inscription</CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <div className="py-8">
              <Loader2 className="mx-auto animate-spin text-neutral-400" size={32} />
              <p className="mt-3 text-sm text-neutral-500">Chargement...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <XCircle className="mx-auto text-red-500" size={40} />
              <p className="mt-3 text-sm text-red-600">{error}</p>
            </div>
          )}

          {status === 'already_cancelled' && (
            <div className="py-8">
              <CheckCircle className="mx-auto text-amber-500" size={40} />
              <p className="mt-3 text-sm text-neutral-600">Cette inscription a déjà été annulée.</p>
            </div>
          )}

          {status === 'ready' && info && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                Vous êtes sur le point d'annuler l'inscription de{' '}
                <strong>{info.first_name} {info.last_name}</strong> pour la session du{' '}
                <strong>{info.session_date}</strong> à <strong>{info.session_time}</strong>.
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <Button
                  data-testid="confirm-cancel-btn"
                  variant="destructive"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Confirmer l'annulation
                </Button>
              </div>
            </div>
          )}

          {status === 'cancelled' && (
            <div className="py-8">
              <CheckCircle className="mx-auto text-emerald-500" size={40} />
              <p className="mt-3 text-sm text-neutral-600">
                Votre inscription a été annulée avec succès.<br />
                L'accueil a été notifié.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
